import React, { useState } from "react";
import OverlayContainer from "./OverlayContainer";
import AlertOverlay from "./AlertOverlay";
import { useAuth } from "../../Context/AuthContext";
import { auth } from "../../Firebase/firebaseConfig";
import { sendPasswordResetEmail } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import userIcon from "../../assets/user.png";
import google from "../../assets/google.svg";
import whiteLoader from "../../assets/load.gif";

const AuthOverlay = ({ stat, toggleOverlay }) => {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, signOut, currentUser } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [alertState, setAlertState] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: null,
    onConfirm: null,
  });

  const openAlert = (options) => {
    setAlertState((prev) => ({
      ...prev,
      open: true,
      confirmText: "OK",
      cancelText: null,
      onConfirm: null,
      ...options,
    }));
  };

  const closeAlert = () => {
    setAlertState((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const handleGoogle = async () => {
    try {
      await signInWithGoogle();
      toggleOverlay();
      openAlert({
        title: "Bienvenue",
        message: "Connexion réussie.",
      });
    } catch (e) {
      openAlert({
        title: "Erreur",
        message: e.message || "Authentification échouée",
      });
    }
  };

  const handleLoginClick = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setLoginError("Veuillez remplir tous les champs");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    try {
      await signInWithEmail(email, password);
      setEmail("");
      setPassword("");
      toggleOverlay();
      openAlert({
        title: "Bienvenue",
        message: "Connexion réussie.",
      });
    } catch (e) {
      setLoginError(e.message || "Identifiants incorrects");
    }
    setLoginLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      setLoginError("Veuillez remplir tous les champs");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    try {
      await signUpWithEmail(email, password, name);
      setEmail("");
      setPassword("");
      setName("");
      toggleOverlay();
      openAlert({
        title: "Bienvenue",
        message: "Votre compte a été créé.",
      });
    } catch (e) {
      setLoginError(e.message || "Erreur lors de l'inscription");
    }
    setLoginLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setLoginError("Veuillez saisir votre email");
      return;
    }
    setLoginLoading(true);
    try {
      const actionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, resetEmail.trim(), actionCodeSettings);
      setResetEmailSent(true);
      setLoginError("");
      setResetEmail("");
    } catch (e) {
      setLoginError(e.message || "Email non trouvé");
    }
    setLoginLoading(false);
  };

  return (
    <>
    <OverlayContainer 
      className={"max-z auth-overlay"}
      contentHeight="70%"
      contentWidth="50%"
      toggleOverlay={toggleOverlay}
      stat={stat}foublie
      children={
        currentUser ? (
          <div className="bold login-container w-full h-full flex-column align-items-center justify-center px-40 py-40">
            <div className="login-panel user-panel">
              <p className="sec-text my-20">Bienvenue, <span className="red">{currentUser.name || currentUser.email}</span> !</p>
              <motion.div className="action-button bg-orange my-20" onClick={() =>
                    openAlert({
                      title: "Confirmation",
                      message: "Voulez-vous vous déconnecter ?",
                      confirmText: "Oui",
                      cancelText: "Non",
                      onConfirm: async () => {
                        await signOut();
                        toggleOverlay();
                      },
                    })
                  }>
                <motion.p className="btn">Se déconnecter</motion.p>
              </motion.div>
            </div>
          </div>
        ) : (
          <div className="bold login-container w-full h-full flex-column align-items-center justify-center px-40 py-40">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                className="login-panel"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {mode === "signin" ? (
              <>
                <p className="sec-text">
                  Bienvenue, Une{" "}
                  <span>
                    autre Fois <span className="orange">!</span>
                  </span>
                </p>
                <p className="label my-20 ">Adresse Email</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Entrez votre adresse email ici"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <img src={userIcon} alt="" className="absolute right0 small-icon" />
                </div>

                <p className="label my-20">Mot de passe</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Entrez votre mot de passe ici"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <img
                    src={userIcon}
                    alt=""
                    className="absolute right0 small-icon pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
                <p
                  className="small-text grey my-10 pointer right-text"
                  onClick={() => {
                    setMode("forgot");
                    setResetEmailSent(false);
                    setLoginError("");
                  }}
                >
                  Mot de passe oublié ?
                </p>

                <div className="relative login-actions display flex justify-space-between align-items-center my-20">
                  <motion.div className="action-button bg-orange">
                    <motion.p
                      className="btn"
                      initial={{ opacity: 1 }}
                      animate={!loginLoading ? { opacity: 1 } : { opacity: 0 }}
                      onClick={handleLoginClick}
                    >
                      Se Connecter
                    </motion.p>
                    <motion.div
                      className="btn-loader absolute flex justify-center align-items-center top0 z-1"
                      initial={{ opacity: 0 }}
                      animate={loginLoading ? { opacity: 1 } : { opacity: 0 }}
                    >
                      <img src={whiteLoader} alt="" />
                    </motion.div>
                  </motion.div>
                </div>

                <p
                  className="small-text grey my-10 pointer"
                  onClick={() => {
                    setMode("signup");
                    setLoginError("");
                    setEmail("");
                    setPassword("");
                    setName("");
                  }}
                >
                  C'est Votre Première Fois Ici? Créer Votre Compte.
                </p>

                <motion.p
                  className="small-text orange my-10"
                  initial={{ opacity: 0 }}
                  animate={loginError ? { opacity: 1 } : { opacity: 0 }}
                >
                  {loginError}
                </motion.p>

                <div className="half-width-hr hr my-20"></div>
                <p className="small-text my-20 flex justify-center">Ou, Utilisez:</p>

                <div className="social-container flex justify-space-evenly align-items-center">
                  <div className="social-login-btn white" onClick={handleGoogle}>
                    <img src={google} alt="" className="mx-10 small-icon" />
                    <p className="btn">Google</p>
                  </div>
                </div>
              </>
            ) : mode === "signup" ? (
              <>
                <p className="sec-text my-20">Créer un <span className="orange">Compte</span></p>
                
                <p className="label my-20">Nom Complet</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Entrez votre nom complet"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <img src={userIcon} alt="" className="absolute right0 small-icon" />
                </div>

                <p className="label my-20">Adresse Email</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Entrez votre adresse email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <img src={userIcon} alt="" className="absolute right0 small-icon" />
                </div>

                <p className="label my-20">Mot de passe</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Créez un mot de passe sécurisé"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <img
                    src={userIcon}
                    alt=""
                    className="absolute right0 small-icon pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>

                <div className="relative login-actions display flex justify-space-between align-items-center my-20">
                  <motion.div className="action-button bg-orange">
                    <motion.p
                      className="btn"
                      initial={{ opacity: 1 }}
                      animate={!loginLoading ? { opacity: 1 } : { opacity: 0 }}
                      onClick={handleSignUp}
                    >
                      S'inscrire
                    </motion.p>
                    <motion.div
                      className="btn-loader absolute flex justify-center align-items-center top0 z-1"
                      initial={{ opacity: 0 }}
                      animate={loginLoading ? { opacity: 1 } : { opacity: 0 }}
                    >
                      <img src={whiteLoader} alt="" />
                    </motion.div>
                  </motion.div>
                </div>

                <p
                  className="small-text grey my-10 pointer"
                  onClick={() => {
                    setMode("signin");
                    setLoginError("");
                    setEmail("");
                    setPassword("");
                    setName("");
                  }}
                >
                  Déjà un compte? Se Connecter.
                </p>

                <motion.p
                  className="small-text red my-10"
                  initial={{ opacity: 0 }}
                  animate={loginError ? { opacity: 1 } : { opacity: 0 }}
                >
                  {loginError}
                </motion.p>
              </>
            ) : (
              <>
                <p className="sec-text my-20">Réinitialiser Votre <span className="red">Mot de passe</span></p>
                
                <p className="label my-20">Email de récupération</p>
                <div className="input-container relative my-10">
                  <input
                    placeholder="Entrez votre email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                  <img src={userIcon} alt="" className="absolute right0 small-icon" />
                </div>
                
                {resetEmailSent && (
                  <p className="small-text green my-10">
                    Lien de réinitialisation envoyé ! Vérifiez votre email.
                  </p>
                )}

                <div className="relative login-actions display flex justify-space-between align-items-center my-20">
                  <motion.div className="action-button bg-orange">
                    <motion.p
                      className="btn"
                      initial={{ opacity: 1 }}
                      animate={!loginLoading ? { opacity: 1 } : { opacity: 0 }}
                      onClick={handleForgotPassword}
                    >
                      Envoyer le lien
                    </motion.p>
                    <motion.div
                      className="btn-loader absolute flex justify-center align-items-center top0 z-1"
                      initial={{ opacity: 0 }}
                      animate={loginLoading ? { opacity: 1 } : { opacity: 0 }}
                    >
                      <img src={whiteLoader} alt="" />
                    </motion.div>
                  </motion.div>
                </div>

                <p
                  className="small-text grey my-10 pointer"
                  onClick={() => {
                    setMode("signin");
                    setResetEmail("");
                    setResetEmailSent(false);
                    setLoginError("");
                  }}
                >
                  Retour à la connexion
                </p>

                <motion.p
                  className="small-text red my-10"
                  initial={{ opacity: 0 }}
                  animate={loginError ? { opacity: 1 } : { opacity: 0 }}
                >
                  {loginError}
                </motion.p>
              </>
            )}
              </motion.div>
            </AnimatePresence>
          </div>
        )
      }
    />
    <AlertOverlay
      stat={alertState.open}
      title={alertState.title}
      message={alertState.message}
      confirmText={alertState.confirmText}
      cancelText={alertState.cancelText}
      onConfirm={alertState.onConfirm}
      onClose={closeAlert}
      autoCloseMs={alertState.cancelText ? null : 2500}
    />
    </>
  );
};

export default AuthOverlay;











